import {
  CapsuleGeometry,
  Color,
  CylinderGeometry,
  Group,
  LatheGeometry,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  SphereGeometry,
  Vector2,
  Vector3,
} from 'three';
import type { ContactPoint, FingerName } from './types';

/**
 * Body units are metres-ish: the figure stands about 1.75 tall with the
 * shoulders at 1.4, which is what the signing-space anchors in pose.ts assume.
 *
 * These four constants and every joint offset below are load-bearing: the arm
 * IK solves against them and the alphabet's contacts are measured from the
 * fingertip joints. The meshes hung off those joints are free to change; the
 * skeleton is not.
 */
export const UPPER_ARM = 0.29;
export const FOREARM = 0.27;
export const SHOULDER_Y = 1.4;
export const SHOULDER_X = 0.19;

/**
 * A signer is read at the hands and the face, so the figure is coloured the
 * way sign-language interpreters dress: skin against a solid, contrasting
 * garment, with no busy detail behind the signing space. The palette stays
 * inside the app's Modernist tokens — the shirt is the ink family, the skin a
 * warm neutral that separates from it at the stage's small size.
 */
const SKIN = new Color('#c8a184');
const SKIN_DARK = new Color('#b08a6d');
const SHIRT = new Color('#3f4a52');
const SHIRT_DARK = new Color('#333c43');
const HAIR = new Color('#2a2422');
const SCLERA = new Color('#efeae6');
const IRIS = new Color('#3a2b23');
const MOUTH = new Color('#7c4f48');

export interface FingerJoints {
  proximal: Object3D;
  middle: Object3D;
  distal: Object3D;
  tip: Object3D;
}

export interface ArmRig {
  side: 'left' | 'right';
  shoulder: Object3D;
  upperArm: Object3D;
  forearm: Object3D;
  wrist: Object3D;
  fingers: Record<FingerName, FingerJoints>;
}

export interface Rig {
  root: Group;
  head: Object3D;
  browLeft: Object3D;
  browRight: Object3D;
  jaw: Object3D;
  arms: { left: ArmRig; right: ArmRig };
}

function material(colour: Color, roughness = 0.72): MeshStandardMaterial {
  return new MeshStandardMaterial({ color: colour, roughness, metalness: 0.02 });
}

/**
 * A limb segment that tapers from joint to joint, the way an arm actually
 * does. Built around the origin hanging down its own -Y, which is what the IK
 * assumes; the end caps double as the joints.
 */
function taperedLimb(length: number, top: number, bottom: number, colour: Color): Object3D {
  const group = new Object3D();
  const material_ = material(colour);

  const shaft = new Mesh(new CylinderGeometry(top, bottom, length, 16, 1, true), material_);
  shaft.position.y = -length / 2;
  group.add(shaft);

  const upper = new Mesh(new SphereGeometry(top, 16, 12), material_);
  group.add(upper);

  const lower = new Mesh(new SphereGeometry(bottom, 16, 12), material_);
  lower.position.y = -length;
  group.add(lower);

  return group;
}

function joint(parent: Object3D, y = 0): Object3D {
  const node = new Object3D();
  node.position.y = y;
  parent.add(node);
  return node;
}

function buildFinger(
  hand: Object3D,
  name: FingerName,
  offsetX: number,
  offsetY: number,
  offsetZ: number,
  lengths: [number, number, number],
): FingerJoints {
  const [l1, l2, l3] = lengths;
  // Fingers taper toward the tip; the thumb is thicker throughout.
  const base = name === 'thumb' ? 0.0118 : 0.0094;

  const proximal = new Object3D();
  proximal.position.set(offsetX, offsetY, offsetZ);
  hand.add(proximal);
  proximal.add(taperedLimb(l1, base, base * 0.93, SKIN));

  const middle = joint(proximal, -l1);
  middle.add(taperedLimb(l2, base * 0.93, base * 0.86, SKIN));

  const distal = joint(middle, -l2);
  distal.add(taperedLimb(l3, base * 0.86, base * 0.72, SKIN));

  const tip = joint(distal, -l3);

  return { proximal, middle, distal, tip };
}

function buildArm(torso: Object3D, side: 'left' | 'right'): ArmRig {
  const mirror = side === 'right' ? 1 : -1;

  const shoulder = new Object3D();
  shoulder.position.set(mirror * SHOULDER_X, SHOULDER_Y, 0);
  torso.add(shoulder);

  const upperArm = new Object3D();
  shoulder.add(upperArm);
  upperArm.add(taperedLimb(UPPER_ARM, 0.048, 0.038, SHIRT));

  // The deltoid rides on the upper arm rather than the torso, so the shoulder
  // reads as a shoulder through the whole range the IK swings it.
  const deltoid = new Mesh(new SphereGeometry(0.058, 18, 14), material(SHIRT));
  deltoid.position.y = -0.034;
  deltoid.scale.set(1, 1.24, 0.95);
  upperArm.add(deltoid);

  const forearm = joint(upperArm, -UPPER_ARM);
  // Bare forearm: the sleeve ends above the elbow, so the arm reads as skin
  // from the elbow down and the hands are never lost against the garment.
  forearm.add(taperedLimb(FOREARM, 0.037, 0.026, SKIN));

  const wrist = joint(forearm, -FOREARM);

  // Palm: a rounded mass the fingers hang off, so the hand reads as a hand
  // from any palm orientation rather than as a card seen edge-on.
  const palm = new Mesh(new CapsuleGeometry(0.030, 0.050, 6, 16), material(SKIN));
  palm.position.set(0, -0.052, 0.002);
  palm.scale.set(1.44, 1, 0.60);
  wrist.add(palm);

  const heel = new Mesh(new SphereGeometry(0.027, 16, 14), material(SKIN));
  heel.position.set(0, -0.014, 0.001);
  heel.scale.set(1.34, 0.86, 0.70);
  wrist.add(heel);

  // The thumb's muscle pad — small, but it is what stops the hand reading as
  // a mitten when the palm faces the viewer.
  const thenar = new Mesh(new SphereGeometry(0.020, 14, 12), material(SKIN));
  thenar.position.set(mirror * 0.024, -0.052, 0.006);
  thenar.scale.set(0.9, 1.5, 0.8);
  wrist.add(thenar);

  const fingers = {
    thumb: buildFinger(wrist, 'thumb', mirror * 0.042, -0.046, 0.01, [0.036, 0.03, 0.024]),
    index: buildFinger(wrist, 'index', mirror * 0.03, -0.093, 0.002, [0.042, 0.027, 0.022]),
    middle: buildFinger(wrist, 'middle', mirror * 0.0098, -0.096, 0.002, [0.046, 0.029, 0.023]),
    ring: buildFinger(wrist, 'ring', mirror * -0.0104, -0.094, 0.002, [0.042, 0.027, 0.021]),
    little: buildFinger(wrist, 'little', mirror * -0.029, -0.088, 0.002, [0.033, 0.022, 0.019]),
  } satisfies Record<FingerName, FingerJoints>;

  return { side, shoulder, upperArm, forearm, wrist, fingers };
}

/** Torso silhouette, revolved: waist in, chest out, shoulders squared off. */
function buildTorso(): Mesh {
  const profile = [
    [0.128, 0.94],
    [0.126, 1.02],
    [0.122, 1.1],
    [0.129, 1.18],
    [0.146, 1.26],
    [0.163, 1.33],
    [0.172, 1.39],
    [0.168, 1.43],
    [0.132, 1.46],
    [0.075, 1.485],
    [0.05, 1.5],
  ].map(([radius, y]) => new Vector2(radius, y));

  const torso = new Mesh(new LatheGeometry(profile, 28), material(SHIRT));
  // A person is wider than deep; revolving gives a cylinder until you say so.
  torso.scale.set(1.12, 1, 0.68);
  return torso;
}

/**
 * The shoulder yoke: a span across the top of the chest that the two deltoids
 * sink into. Without it each deltoid reads as a ball bolted to a narrower
 * torso, with a crease where the sphere meets the revolved surface.
 */
function buildYoke(): Mesh {
  // Must reach past the chest's own half-width (~0.19) to read at all — sized
  // inside it, the yoke is simply swallowed by the torso.
  const yoke = new Mesh(new CapsuleGeometry(0.072, 0.26, 6, 20), material(SHIRT));
  yoke.position.set(0, SHOULDER_Y - 0.012, 0.004);
  yoke.rotation.z = Math.PI / 2;
  yoke.scale.set(1, 1, 0.86);
  return yoke;
}

function buildHead(): {
  head: Object3D;
  browLeft: Object3D;
  browRight: Object3D;
  jaw: Object3D;
} {
  const head = new Object3D();
  head.position.y = 1.55;

  const skin = material(SKIN, 0.66);

  /**
   * The head as one revolved surface, chin to crown. Two intersecting spheres
   * are quicker but leave a crease exactly where the jaw meets the cheek —
   * which lands at mouth height and reads as a drawn-on moustache.
   */
  const profile = [
    [0.004, -0.014],
    [0.032, 0.004],
    [0.055, 0.024],
    [0.071, 0.046],
    [0.082, 0.066],
    [0.09, 0.086],
    [0.096, 0.106],
    [0.098, 0.126],
    [0.0955, 0.15],
    [0.086, 0.174],
    [0.066, 0.196],
    [0.034, 0.212],
    [0.004, 0.218],
  ].map(([radius, y]) => new Vector2(radius, y));

  const skull = new Mesh(new LatheGeometry(profile, 30), skin);
  // A head is deeper than it is wide; revolving alone gives a column.
  skull.scale.set(1, 1, 1.1);
  head.add(skull);

  const nose = new Mesh(new SphereGeometry(0.0155, 14, 12), skin);
  nose.position.set(0, 0.086, 0.0965);
  nose.scale.set(0.8, 1.3, 1.3);
  head.add(nose);

  for (const side of [-1, 1]) {
    const ear = new Mesh(new SphereGeometry(0.019, 14, 12), skin);
    ear.position.set(side * 0.092, 0.1, 0.004);
    ear.scale.set(0.42, 1.15, 0.85);
    head.add(ear);

    // Eye: sclera with an iris set into it, so there is a gaze to read. The
    // eyeball sits just proud of the brow ridge — set flush it disappears
    // inside the skull, set deep it reads as a hole.
    const eye = new Mesh(new SphereGeometry(0.0142, 16, 14), material(SCLERA, 0.35));
    eye.position.set(side * 0.038, 0.106, 0.0985);
    eye.scale.set(1, 0.74, 0.6);
    head.add(eye);

    const iris = new Mesh(new SphereGeometry(0.0074, 14, 12), material(IRIS, 0.3));
    iris.position.set(side * 0.038, 0.1055, 0.105);
    iris.scale.set(1, 1, 0.5);
    head.add(iris);

    // An upper lid, which keeps the eyes from reading as startled beads.
    const lid = new Mesh(new SphereGeometry(0.0155, 16, 14), skin);
    lid.position.set(side * 0.038, 0.1145, 0.0965);
    lid.scale.set(1.02, 0.62, 0.62);
    head.add(lid);
  }

  /**
   * Hair follows the head's own profile rather than being a sphere laid over
   * it — a sphere cutting a lathe leaves a zigzag hairline where the two
   * tessellations disagree.
   *
   * The hairline has to clear the brows: they carry Auslan grammar, and hair
   * hanging into them would hide the question face the stage depends on.
   */
  const hairMaterial = material(HAIR, 0.85);
  const lathe = (points: number[][], zOffset = 0): Mesh => {
    const mesh = new Mesh(
      new LatheGeometry(points.map(([r, y]) => new Vector2(r, y)), 30),
      hairMaterial,
    );
    mesh.scale.set(1, 1, 1.1);
    mesh.position.z = zOffset;
    return mesh;
  };

  head.add(
    lathe([
      [0.101, 0.146],
      [0.1005, 0.152],
      [0.091, 0.176],
      [0.071, 0.197],
      [0.039, 0.213],
      [0.008, 0.22],
    ]),
  );

  // Sides and back, shifted back so it is swallowed by the head at the face
  // and only shows from the ears round — hair below the hairline, no cap edge.
  head.add(
    lathe(
      [
        [0.094, 0.082],
        [0.1005, 0.106],
        [0.1025, 0.126],
        [0.1, 0.15],
        [0.0975, 0.162],
      ],
      -0.015,
    ),
  );

  const lips = new Mesh(new SphereGeometry(0.019, 18, 14), material(MOUTH, 0.5));
  lips.position.set(0, 0.05, 0.0755);
  lips.scale.set(1.1, 0.38, 0.36);
  head.add(lips);

  // Brows and jaw carry the non-manual features; the animator sets their
  // position and scale directly, so these are single meshes, not groups.
  const browGeometry = new CapsuleGeometry(0.0062, 0.036, 5, 10);
  const browMaterial = material(HAIR, 0.8);

  const browLeft = new Mesh(browGeometry, browMaterial);
  browLeft.position.set(-0.042, 0.136, 0.094);
  browLeft.rotation.z = Math.PI / 2;
  browLeft.scale.set(1, 1, 0.62);
  head.add(browLeft);

  const browRight = new Mesh(browGeometry, browMaterial);
  browRight.position.set(0.042, 0.136, 0.094);
  browRight.rotation.z = Math.PI / 2;
  browRight.scale.set(1, 1, 0.62);
  head.add(browRight);

  // The mouth opening: scaled on its Y by the animator for mouthOpen.
  const jaw = new Mesh(new SphereGeometry(0.017, 18, 14), material(new Color('#3d211f'), 0.6));
  jaw.position.set(0, 0.05, 0.0785);
  jaw.scale.set(0.92, 0.15, 0.3);
  head.add(jaw);

  return { head, browLeft, browRight, jaw };
}

export function buildRig(): Rig {
  const root = new Group();

  const hips = new Mesh(new CapsuleGeometry(0.132, 0.14, 6, 18), material(SHIRT_DARK));
  hips.position.y = 0.98;
  hips.scale.set(1.1, 1, 0.7);
  root.add(hips);

  root.add(buildTorso());
  root.add(buildYoke());

  const neck = new Mesh(new CylinderGeometry(0.040, 0.056, 0.105, 16), material(SKIN_DARK, 0.7));
  neck.position.y = 1.508;
  root.add(neck);

  const { head, browLeft, browRight, jaw } = buildHead();
  root.add(head);

  const arms = {
    left: buildArm(root, 'left'),
    right: buildArm(root, 'right'),
  };

  return { root, head, browLeft, browRight, jaw, arms };
}

/** World-space position of a fingertip, used to resolve `weakHand` contact. */
export function fingertipWorld(arm: ArmRig, finger: FingerName, into: Vector3): Vector3 {
  return arm.fingers[finger].tip.getWorldPosition(into);
}

/** Scratch for contactPointWorld only — never reused across concurrent calls. */
const contactScratch = new Vector3();

/** Stacking one fist on the other is a world-up relationship, not a wrist-local one. */
const FIST_TOP = new Vector3(0, 0.07, 0);

/**
 * Wrist-local offsets for the named palm regions. The fingers hang down the
 * wrist's local -Y and the palm faces its local +Z (see applyHandShape), so
 * the palm centre sits below the wrist and just proud of the surface.
 */
const CONTACT_LOCAL: Record<'palm' | 'heel', [number, number, number]> = {
  palm: [0, -0.075, 0.02],
  heel: [0, -0.025, 0.02],
};

/**
 * World-space position of a named contact point on a hand. Fingertips come
 * from the articulated joints; palm regions from the wrist's frame; the
 * webbing from the thumb and index bases; the fist top is straight above the
 * wrist, which is what stacking one fist on another needs.
 */
export function contactPointWorld(arm: ArmRig, point: ContactPoint, into: Vector3): Vector3 {
  if (point === 'web') {
    arm.fingers.thumb.proximal.getWorldPosition(into);
    arm.fingers.index.proximal.getWorldPosition(contactScratch);
    return into.lerp(contactScratch, 0.5);
  }
  if (point === 'fist') {
    return arm.wrist.getWorldPosition(into).add(FIST_TOP);
  }
  if (point === 'palm' || point === 'heel') {
    const [x, y, z] = CONTACT_LOCAL[point];
    return arm.wrist.localToWorld(into.set(x, y, z));
  }
  return fingertipWorld(arm, point, into);
}
